import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  REVIEW_TASK_STATUS,
  REVIEW_FALLBACK_GROUP_LABEL,
  REVIEW_TASK_KIND,
  REVIEW_VISIBLE_TASK_STATUSES,
} from "../../constants/reviewConstants";
import { areFieldValuesEqual } from "../InputField/helper";

const ReviewNavigationContext = createContext(null);

const TASK_KIND_DEFAULT_WIZARD = {
  [REVIEW_TASK_KIND.FIELD_CLARIFICATION]: "",
  [REVIEW_TASK_KIND.EXTRA_QUESTION]: "ExtraQuestions",
  [REVIEW_TASK_KIND.DOCUMENT_REQUEST]: "ApplicationDocRedo",
};

const TASK_KIND_DEFAULT_LABEL = {
  [REVIEW_TASK_KIND.FIELD_CLARIFICATION]: "Requested item",
  [REVIEW_TASK_KIND.EXTRA_QUESTION]: "Requested information",
  [REVIEW_TASK_KIND.DOCUMENT_REQUEST]: "Requested document",
};

const normalizeTaskFieldPath = (path = "") => String(path || "")
  .replace(/\[(\d+)\]/g, "[]")
  .replace(/\.([0-9]+)(?=\.|$)/g, "[]");

const normalizeFieldIdSegment = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

const buildCompositeRowKey = (values = []) => {
  const parts = (Array.isArray(values) ? values : [values])
    .map((value) => normalizeFieldIdSegment(value))
    .filter(Boolean);

  if (!parts.length) {
    return null;
  }

  return parts.join("-");
};

const isArrayTaskFieldPath = (path = "") => normalizeTaskFieldPath(path).includes("[]");

const getArrayTaskBasePath = (normalizedFieldPath = "") => {
  const normalized = normalizeTaskFieldPath(normalizedFieldPath);
  if (!normalized.includes("[]")) {
    return "";
  }

  return normalized.split("[]")[0].replace(/\.$/, "");
};

const getArrayTaskCollectionName = (normalizedFieldPath = "") => {
  const basePath = getArrayTaskBasePath(normalizedFieldPath);
  if (!basePath) {
    return "";
  }

  const segments = basePath.split(".");
  return segments[segments.length - 1] || "";
};

const getArrayTaskFieldSuffix = (normalizedFieldPath = "") => {
  const normalized = normalizeTaskFieldPath(normalizedFieldPath);
  if (!normalized.includes("[]")) {
    return "";
  }

  return normalized
    .split("[]")
    .slice(1)
    .join("[]")
    .replace(/^\./, "")
    .replace(/\./g, "-");
};

const getTaskWizardFromFieldId = (fieldId = "") => String(fieldId || "").split("-")?.[0] || "";

const extractArrayTaskRowToken = ({ fieldId = "", wizard = "", normalizedFieldPath = "" }) => {
  if (!fieldId || !wizard) {
    return null;
  }

  const collectionName = getArrayTaskCollectionName(normalizedFieldPath);
  if (!collectionName) {
    return null;
  }

  const prefix = `${wizard}-${collectionName}-`;
  if (!String(fieldId).startsWith(prefix)) {
    return null;
  }

  const suffix = getArrayTaskFieldSuffix(normalizedFieldPath);
  const suffixToken = suffix ? `-${suffix}` : "";
  if (suffixToken && !String(fieldId).endsWith(suffixToken)) {
    return null;
  }

  const endIndex = suffixToken ? fieldId.length - suffixToken.length : fieldId.length;
  if (endIndex <= prefix.length) {
    return null;
  }

  return fieldId.slice(prefix.length, endIndex) || null;
};

const getArrayTaskTokenOrderValue = (token = "") => {
  const normalized = String(token || "").trim().toLowerCase();
  if (!/^\d+$/.test(normalized)) {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const getValueByPath = (data = {}, path = "") => {
  if (!path) {
    return undefined;
  }

  return String(path)
    .split(".")
    .filter(Boolean)
    .reduce((acc, segment) => (acc === undefined || acc === null ? undefined : acc?.[segment]), data);
};

const resolveReviewIndividualRowKey = (rowData = {}) => (
  buildCompositeRowKey([rowData?._id])
  || buildCompositeRowKey([rowData?.aEId])
  || buildCompositeRowKey([rowData?.cyclosUserId])
);

const resolveReviewEntityRowKey = (rowData = {}, fallbackRowKey = null) => (
  buildCompositeRowKey([rowData?.aEId])
  || buildCompositeRowKey([rowData?._id])
  || fallbackRowKey
);

const resolveReviewAddressRowKey = (rowData = {}) => (
  buildCompositeRowKey([rowData?.type])
  || buildCompositeRowKey([rowData?._id])
);

const resolveReviewArrayRowKey = (arrayBasePath = "", rowData = {}, index = 0) => {
  const arrayFieldName = String(arrayBasePath || "").split(".").pop();

  if (arrayFieldName === "individuals") {
    return resolveReviewIndividualRowKey(rowData) || String(index);
  }

  if (arrayFieldName === "entities") {
    return resolveReviewEntityRowKey(rowData, String(index)) || String(index);
  }

  if (arrayFieldName === "addressList") {
    return resolveReviewAddressRowKey(rowData) || String(index);
  }

  return buildCompositeRowKey([rowData?._id]) || String(index);
};

const buildArrayRowOrderMaps = (fieldDefinitions = {}, formData = null) => {
  const orderMaps = new Map();

  Object.entries(fieldDefinitions || {}).forEach(([fieldKey, fieldDefinition]) => {
    const normalizedFieldPath = normalizeTaskFieldPath(fieldDefinition?.field || fieldKey);
    const arrayBasePath = getArrayTaskBasePath(normalizedFieldPath);

    if (!arrayBasePath || orderMaps.has(arrayBasePath)) {
      return;
    }

    const collection = getValueByPath(formData, arrayBasePath);
    const tokenOrderMap = new Map();

    if (Array.isArray(collection)) {
      collection.forEach((rowData, index) => {
        const rowToken = resolveReviewArrayRowKey(arrayBasePath, rowData, index);
        if (rowToken !== null && rowToken !== undefined && !tokenOrderMap.has(String(rowToken))) {
          tokenOrderMap.set(String(rowToken), index);
        }
      });
    }

    orderMaps.set(arrayBasePath, tokenOrderMap);
  });

  return orderMaps;
};

const getOrderedArrayTaskTokens = (tasks = [], wizard = "", normalizedFieldPath = "") => {
  if (!wizard) {
    return [];
  }

  const arrayBasePath = getArrayTaskBasePath(normalizedFieldPath);
  if (!arrayBasePath) {
    return [];
  }

  const seenTokens = new Map();

  (Array.isArray(tasks) ? tasks : []).forEach((task, index) => {
    const taskFieldPath = normalizeTaskFieldPath(task?.fieldPath);
    if (
      task?.kind !== REVIEW_TASK_KIND.FIELD_CLARIFICATION
      || !taskFieldPath.startsWith(`${arrayBasePath}[]`)
      || getTaskWizardFromFieldId(task?.fieldId) !== wizard
    ) {
      return;
    }

    const rowToken = extractArrayTaskRowToken({
      fieldId: task?.fieldId,
      wizard,
      normalizedFieldPath: taskFieldPath,
    });

    if (!rowToken || seenTokens.has(rowToken)) {
      return;
    }

    seenTokens.set(rowToken, {
      token: rowToken,
      firstSeenIndex: index,
      orderValue: getArrayTaskTokenOrderValue(rowToken),
    });
  });

  return Array.from(seenTokens.values())
    .sort((left, right) => {
      const leftHasOrder = left.orderValue !== null;
      const rightHasOrder = right.orderValue !== null;

      if (leftHasOrder && rightHasOrder && left.orderValue !== right.orderValue) {
        return left.orderValue - right.orderValue;
      }

      if (leftHasOrder !== rightHasOrder) {
        return leftHasOrder ? -1 : 1;
      }

      return left.firstSeenIndex - right.firstSeenIndex;
    })
    .map((entry) => entry.token);
};

const resolveMatchingFieldTasks = (
  tasks = [],
  normalizedFieldPath = "",
  fieldId = "",
  options = {}
) => {
  const { wizard = "" } = options;
  const pathMatchedTasks = (Array.isArray(tasks) ? tasks : []).filter((task) => (
    task?.kind === REVIEW_TASK_KIND.FIELD_CLARIFICATION
    && normalizeTaskFieldPath(task?.fieldPath) === normalizedFieldPath
    && REVIEW_VISIBLE_TASK_STATUSES.includes(task?.status)
  ));

  if (!pathMatchedTasks.length) {
    return [];
  }

  const exactFieldIdMatchedTasks = fieldId
    ? pathMatchedTasks.filter((task) => task?.fieldId === fieldId)
    : [];

  if (exactFieldIdMatchedTasks.length) {
    return exactFieldIdMatchedTasks;
  }

  if (!isArrayTaskFieldPath(normalizedFieldPath)) {
    return pathMatchedTasks;
  }

  const currentRowToken = extractArrayTaskRowToken({
    fieldId,
    wizard,
    normalizedFieldPath,
  });

  if (currentRowToken) {
    const rowTokenMatchedTasks = pathMatchedTasks.filter((task) => (
      extractArrayTaskRowToken({
        fieldId: task?.fieldId,
        wizard,
        normalizedFieldPath,
      }) === currentRowToken
    ));

    if (rowTokenMatchedTasks.length) {
      return rowTokenMatchedTasks;
    }

    return [];
  }

  const legacyPathMatchedTasks = pathMatchedTasks.filter((task) => !task?.fieldId);
  return legacyPathMatchedTasks.length === 1 ? legacyPathMatchedTasks : [];
};

export const formatReviewWizardLabel = (wizard = "") => {
  if (!wizard) {
    return REVIEW_FALLBACK_GROUP_LABEL;
  }

  const normalized = String(wizard)
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();

  return normalized || REVIEW_FALLBACK_GROUP_LABEL;
};

const resolveTaskFieldDefinition = (fieldDefinitions = {}, fieldPath = "") => {
  if (!fieldPath || !fieldDefinitions || typeof fieldDefinitions !== "object") {
    return null;
  }

  const normalizedFieldPath = normalizeTaskFieldPath(fieldPath);
  return fieldDefinitions[fieldPath] || fieldDefinitions[normalizedFieldPath] || null;
};

const buildReviewFieldOrderMaps = (fieldDefinitions = {}) => {
  if (!fieldDefinitions || typeof fieldDefinitions !== "object") {
    return {
      fieldOrderMap: new Map(),
      arrayCollectionOrderMap: new Map(),
    };
  }

  const orderMap = new Map();
  const arrayCollectionOrderMap = new Map();
  let nextOrder = 0;

  Object.entries(fieldDefinitions).forEach(([fieldKey, fieldDefinition]) => {
    if (!fieldDefinition || typeof fieldDefinition !== "object") {
      return;
    }

    const canonicalFieldPath = normalizeTaskFieldPath(fieldDefinition?.field || fieldKey);
    if (!canonicalFieldPath) {
      return;
    }

    if (!orderMap.has(canonicalFieldPath)) {
      orderMap.set(canonicalFieldPath, nextOrder);
      const arrayBasePath = getArrayTaskBasePath(canonicalFieldPath);
      if (arrayBasePath && !arrayCollectionOrderMap.has(arrayBasePath)) {
        arrayCollectionOrderMap.set(arrayBasePath, nextOrder);
      }
      nextOrder += 1;
    }

    const canonicalOrder = orderMap.get(canonicalFieldPath);
    [
      fieldKey,
      fieldDefinition?.field,
      fieldDefinition?.fvBankProperty,
    ].forEach((candidatePath) => {
      const normalizedCandidatePath = normalizeTaskFieldPath(candidatePath);
      if (normalizedCandidatePath && !orderMap.has(normalizedCandidatePath)) {
        orderMap.set(normalizedCandidatePath, canonicalOrder);
      }
    });
  });

  return {
    fieldOrderMap: orderMap,
    arrayCollectionOrderMap,
  };
};

const deriveVisibleReviewTasks = (
  formReview,
  fieldDefinitions,
  fieldOrderMap,
  arrayCollectionOrderMap,
  arrayRowOrderMaps,
  currentWizard = "",
  currentWizardDomFieldOrderMap = new Map(),
  wizardOrderMap = new Map(),
) => {
  const tasks = Array.isArray(formReview?.tasks) ? formReview.tasks : [];

  return tasks
    .filter((task) => (
      Object.values(REVIEW_TASK_KIND).includes(task?.kind)
      && REVIEW_VISIBLE_TASK_STATUSES.includes(task?.status)
    ))
    .map((task, index) => {
      const derivedTask = withDerivedTaskMeta(task, fieldDefinitions);
      const normalizedFieldPath = normalizeTaskFieldPath(derivedTask?.fieldPath || "");
      const fieldOrder = derivedTask?.kind === REVIEW_TASK_KIND.FIELD_CLARIFICATION
        ? fieldOrderMap.get(normalizedFieldPath)
        : undefined;
      const arrayBasePath = isArrayTaskFieldPath(normalizedFieldPath)
        ? getArrayTaskBasePath(normalizedFieldPath)
        : "";
      const rowToken = arrayBasePath
        ? extractArrayTaskRowToken({
          fieldId: derivedTask?.fieldId,
          wizard: derivedTask?.wizard,
          normalizedFieldPath,
        })
        : null;
      const rowOrderFromData = arrayBasePath
        ? arrayRowOrderMaps?.get(arrayBasePath)?.get(String(rowToken))
        : undefined;
      const fallbackOrderedTokens = arrayBasePath
        ? getOrderedArrayTaskTokens(tasks, derivedTask?.wizard, normalizedFieldPath)
        : [];
      const fallbackRowOrder = rowToken ? fallbackOrderedTokens.indexOf(rowToken) : -1;
      const resolvedRowOrder = Number.isFinite(rowOrderFromData)
        ? rowOrderFromData
        : (fallbackRowOrder >= 0 ? fallbackRowOrder : undefined);
      const collectionOrder = arrayBasePath
        ? arrayCollectionOrderMap.get(arrayBasePath)
        : fieldOrder;
      const domFieldOrder = currentWizardDomFieldOrderMap.get(String(derivedTask?.fieldId || ""));

      // Position of the task's wizard in the form's actual navigation sequence
      // (the fieldId is prefixed with its wizard, e.g. "BusinessType-...").
      const taskWizard = getTaskWizardFromFieldId(derivedTask?.fieldId);
      const wizardOrder = taskWizard && wizardOrderMap.has(taskWizard)
        ? wizardOrderMap.get(taskWizard)
        : undefined;

      return {
        ...derivedTask,
        reviewTaskOriginalIndex: index,
        reviewTaskWizardOrder: wizardOrder,
        reviewTaskFieldOrder: fieldOrder,
        reviewTaskArrayBasePath: arrayBasePath,
        reviewTaskArrayCollectionOrder: collectionOrder,
        reviewTaskArrayRowOrder: resolvedRowOrder,
        reviewTaskDomFieldOrder: domFieldOrder,
      };
    })
    .sort((leftTask, rightTask) => {
      // Tasks on different wizards follow the form's navigation sequence first;
      // tasks on the same wizard fall through to DOM/field ordering below.
      const leftHasWizardOrder = Number.isFinite(leftTask?.reviewTaskWizardOrder);
      const rightHasWizardOrder = Number.isFinite(rightTask?.reviewTaskWizardOrder);
      if (
        leftHasWizardOrder
        && rightHasWizardOrder
        && leftTask.reviewTaskWizardOrder !== rightTask.reviewTaskWizardOrder
      ) {
        return leftTask.reviewTaskWizardOrder - rightTask.reviewTaskWizardOrder;
      }

      const leftHasDomOrder = Number.isFinite(leftTask?.reviewTaskDomFieldOrder);
      const rightHasDomOrder = Number.isFinite(rightTask?.reviewTaskDomFieldOrder);

      if (
        leftHasDomOrder
        && rightHasDomOrder
        && leftTask.reviewTaskDomFieldOrder !== rightTask.reviewTaskDomFieldOrder
      ) {
        return leftTask.reviewTaskDomFieldOrder - rightTask.reviewTaskDomFieldOrder;
      }

      const leftCollectionOrder = leftTask?.reviewTaskArrayBasePath
        ? leftTask?.reviewTaskArrayCollectionOrder
        : leftTask?.reviewTaskFieldOrder;
      const rightCollectionOrder = rightTask?.reviewTaskArrayBasePath
        ? rightTask?.reviewTaskArrayCollectionOrder
        : rightTask?.reviewTaskFieldOrder;
      const leftHasCollectionOrder = Number.isFinite(leftCollectionOrder);
      const rightHasCollectionOrder = Number.isFinite(rightCollectionOrder);

      if (leftHasCollectionOrder && rightHasCollectionOrder && leftCollectionOrder !== rightCollectionOrder) {
        return leftCollectionOrder - rightCollectionOrder;
      }

      if (leftHasCollectionOrder !== rightHasCollectionOrder) {
        return leftHasCollectionOrder ? -1 : 1;
      }

      if (
        leftTask?.reviewTaskArrayBasePath
        && leftTask.reviewTaskArrayBasePath === rightTask?.reviewTaskArrayBasePath
      ) {
        const leftHasRowOrder = Number.isFinite(leftTask?.reviewTaskArrayRowOrder);
        const rightHasRowOrder = Number.isFinite(rightTask?.reviewTaskArrayRowOrder);

        if (leftHasRowOrder && rightHasRowOrder && leftTask.reviewTaskArrayRowOrder !== rightTask.reviewTaskArrayRowOrder) {
          return leftTask.reviewTaskArrayRowOrder - rightTask.reviewTaskArrayRowOrder;
        }

        if (leftHasRowOrder !== rightHasRowOrder) {
          return leftHasRowOrder ? -1 : 1;
        }
      }

      const leftHasFieldOrder = Number.isFinite(leftTask?.reviewTaskFieldOrder);
      const rightHasFieldOrder = Number.isFinite(rightTask?.reviewTaskFieldOrder);

      if (leftHasFieldOrder && rightHasFieldOrder && leftTask.reviewTaskFieldOrder !== rightTask.reviewTaskFieldOrder) {
        return leftTask.reviewTaskFieldOrder - rightTask.reviewTaskFieldOrder;
      }

      if (leftHasFieldOrder !== rightHasFieldOrder) {
        return leftHasFieldOrder ? -1 : 1;
      }

      return (leftTask?.reviewTaskOriginalIndex || 0) - (rightTask?.reviewTaskOriginalIndex || 0);
    });
};

const getTaskDefaultWizard = (task = {}) => (
  TASK_KIND_DEFAULT_WIZARD[task?.kind] || ""
);

const getTaskDefaultLabel = (task = {}) => {
  if (task?.kind === REVIEW_TASK_KIND.EXTRA_QUESTION) {
    return task?.title || task?.question || task?.remark || TASK_KIND_DEFAULT_LABEL[task.kind];
  }

  if (task?.kind === REVIEW_TASK_KIND.DOCUMENT_REQUEST) {
    return task?.documentLabel
      || task?.title
      || task?.fieldPath
      || task?.remark
      || TASK_KIND_DEFAULT_LABEL[task.kind];
  }

  return task?.fieldPath || task?.fieldId || TASK_KIND_DEFAULT_LABEL[task?.kind] || "Requested item";
};

export const buildReviewTaskKey = (task = {}) => [
  task?.kind || "task",
  normalizeTaskFieldPath(task?.fieldPath || ""),
  task?.fieldId || "",
  task?.createdAt || "",
].join(":");

const withDerivedTaskMeta = (task = {}, fieldDefinitions = {}) => {
  const fieldDefinition = resolveTaskFieldDefinition(fieldDefinitions, task?.fieldPath);
  const wizard = fieldDefinition?.wizard
    || task?.wizard
    || getTaskDefaultWizard(task)
    || task?.fieldId?.split("-")?.[0]
    || "";

  return {
    ...task,
    fieldDefinition,
    wizard,
    wizardLabel: task?.wizardLabel || formatReviewWizardLabel(wizard),
    fieldLabel: task?.fieldLabel || fieldDefinition?.label || getTaskDefaultLabel(task),
    taskKey: task?.taskKey || buildReviewTaskKey(task),
  };
};

export const groupReviewTasksByWizard = (tasks = []) => {
  const grouped = new Map();

  (Array.isArray(tasks) ? tasks : []).forEach((task) => {
    const key = task?.wizard || "__other__";
    if (!grouped.has(key)) {
      grouped.set(key, {
        key,
        label: task?.wizardLabel || REVIEW_FALLBACK_GROUP_LABEL,
        tasks: [],
      });
    }

    grouped.get(key).tasks.push(task);
  });

  return Array.from(grouped.values());
};

export const isEditableReviewShortcutTarget = (target) => {
  if (!target || typeof target.closest !== "function") {
    return false;
  }

  return Boolean(
    target.closest(
      'input, textarea, select, [contenteditable="true"], [contenteditable=""], .react-datepicker, .react-select__input'
    )
  );
};

const scheduleReviewFieldFocus = (fieldId, attempt = 0) => {
  if (!fieldId || typeof document === "undefined") {
    return;
  }

  const target = document.querySelector(`[data-review-field-id="${fieldId}"]`)
    || document.getElementById(fieldId);

  if (target) {
    const activeElement = document.activeElement;
    const focusTarget = target.matches?.("input, textarea, select, button")
      ? target
      : target.querySelector?.("input, textarea, select, button");

    if (
      activeElement
      && focusTarget
      && activeElement !== focusTarget
      && isEditableReviewShortcutTarget(activeElement)
    ) {
      return;
    }

    target.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });

    focusTarget?.focus?.({ preventScroll: true });
    return;
  }

  if (attempt >= 7) {
    return;
  }

  window.setTimeout(() => scheduleReviewFieldFocus(fieldId, attempt + 1), 140);
};

export const ReviewNavigationProvider = ({ value, children }) => (
  <ReviewNavigationContext.Provider value={value}>
    {children}
  </ReviewNavigationContext.Provider>
);

export const useReviewNavigationContext = () => useContext(ReviewNavigationContext);

export const useReviewNavigation = ({
  formReview,
  setFormReview,
  persistFormReview,
  fieldDefinitions = {},
  formData = null,
  currentWizard = "",
  wizardOrder = [],
  onNavigateToWizard,
  onPersistError,
}) => {
  const formReviewRef = useRef(formReview);
  // Maps each wizard key to its index in the form's navigation sequence so the
  // review issues are numbered/ordered the way the user walks the form.
  const wizardOrderMap = useMemo(() => {
    const map = new Map();
    (Array.isArray(wizardOrder) ? wizardOrder : []).forEach((wizardKey, index) => {
      if (wizardKey && !map.has(wizardKey)) map.set(wizardKey, index);
    });
    return map;
  }, [wizardOrder]);
  const [selectedTaskKey, setSelectedTaskKey] = useState("");
  const [currentWizardDomOrderState, setCurrentWizardDomOrderState] = useState({
    wizard: "",
    signature: "",
    entries: [],
    ready: false,
  });
  const {
    fieldOrderMap,
    arrayCollectionOrderMap,
  } = useMemo(
    () => buildReviewFieldOrderMaps(fieldDefinitions),
    [fieldDefinitions]
  );
  const arrayRowOrderMaps = useMemo(
    () => buildArrayRowOrderMaps(fieldDefinitions, formData),
    [fieldDefinitions, formData],
  );
  const currentWizardDomFieldOrderMap = useMemo(
    () => new Map(currentWizardDomOrderState?.entries || []),
    [currentWizardDomOrderState?.entries]
  );
  const hasFieldDefinitionsLoaded = useMemo(() => (
    Boolean(fieldDefinitions)
    && Object.values(fieldDefinitions).some((fieldDefinition) => (
      fieldDefinition
      && typeof fieldDefinition === "object"
      && typeof fieldDefinition?.field === "string"
      && fieldDefinition.field.trim()
    ))
  ), [fieldDefinitions]);
  const isCurrentWizardDomOrderReady = currentWizardDomOrderState?.wizard === (currentWizard || "")
    && currentWizardDomOrderState?.ready;
  const isCurrentPageTask = useCallback((task) => {
    if (!task) {
      return false;
    }

    const taskFieldId = String(task?.fieldId || "");
    return (
      (task?.wizard || "") === (currentWizard || "")
      || currentWizardDomFieldOrderMap.has(taskFieldId)
    );
  }, [currentWizard, currentWizardDomFieldOrderMap]);

  useEffect(() => {
    formReviewRef.current = formReview;
  }, [formReview]);

  const visibleTasks = useMemo(() => {
    return deriveVisibleReviewTasks(
      formReview,
      fieldDefinitions,
      fieldOrderMap,
      arrayCollectionOrderMap,
      arrayRowOrderMaps,
      currentWizard,
      currentWizardDomFieldOrderMap,
      wizardOrderMap,
    );
  }, [
    arrayCollectionOrderMap,
    arrayRowOrderMaps,
    currentWizard,
    currentWizardDomFieldOrderMap,
    fieldDefinitions,
    fieldOrderMap,
    formReview,
    wizardOrderMap,
  ]);

  const actionableTasks = useMemo(
    () => visibleTasks.filter((task) => task?.status === REVIEW_TASK_STATUS.ACTIONABLE),
    [visibleTasks]
  );
  const completedTasks = useMemo(
    () => visibleTasks.filter((task) => task?.status === REVIEW_TASK_STATUS.COMPLETED),
    [visibleTasks]
  );
  const currentWizardVisibleTasks = useMemo(
    () => visibleTasks.filter((task) => isCurrentPageTask(task)),
    [isCurrentPageTask, visibleTasks]
  );
  const currentWizardActionableTasks = useMemo(
    () => currentWizardVisibleTasks.filter((task) => task?.status === REVIEW_TASK_STATUS.ACTIONABLE),
    [currentWizardVisibleTasks]
  );
  const navigationTasks = useMemo(
    () => visibleTasks,
    [visibleTasks]
  );
  const groupedTasks = useMemo(() => groupReviewTasksByWizard(visibleTasks), [visibleTasks]);
  const hasFieldClarificationTasks = useMemo(
    () => visibleTasks.some((task) => task?.kind === REVIEW_TASK_KIND.FIELD_CLARIFICATION),
    [visibleTasks]
  );
  const isInitialSelectionReady = isCurrentWizardDomOrderReady
    && (!hasFieldClarificationTasks || hasFieldDefinitionsLoaded);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    let isCancelled = false;
    let animationFrameId = 0;
    let secondAnimationFrameId = 0;
    let timeoutId = 0;
    let pollAttempts = 0;
    const wizardKey = currentWizard || "";
    const MAX_DOM_ORDER_POLL_ATTEMPTS = 8;
    const DOM_ORDER_POLL_DELAY_MS = 120;

    const collectDomFieldOrder = () => {
      if (isCancelled) {
        return;
      }

      const nextEntries = [];
      const seenFieldIds = new Set();

      document.querySelectorAll("[data-review-field-id]").forEach((element) => {
        const fieldId = String(element?.getAttribute?.("data-review-field-id") || "").trim();
        if (!fieldId || seenFieldIds.has(fieldId)) {
          return;
        }

        seenFieldIds.add(fieldId);
        nextEntries.push([fieldId, nextEntries.length]);
      });

      const shouldKeepPolling = nextEntries.length === 0 && pollAttempts < MAX_DOM_ORDER_POLL_ATTEMPTS;
      const signature = nextEntries.map(([fieldId, order]) => `${fieldId}:${order}`).join("|");

      setCurrentWizardDomOrderState((previousState) => {
        if (
          previousState?.wizard === wizardKey
          && previousState?.signature === signature
          && previousState?.ready === !shouldKeepPolling
        ) {
          return previousState;
        }

        return {
          wizard: wizardKey,
          signature,
          entries: nextEntries,
          ready: !shouldKeepPolling,
        };
      });

      if (shouldKeepPolling) {
        pollAttempts += 1;
        timeoutId = window.setTimeout(collectDomFieldOrder, DOM_ORDER_POLL_DELAY_MS);
      }
    };

    setCurrentWizardDomOrderState((previousState) => {
      if (previousState?.wizard === wizardKey && previousState?.ready === false) {
        return previousState;
      }

      return {
        wizard: wizardKey,
        signature: "",
        entries: [],
        ready: false,
      };
    });

    animationFrameId = window.requestAnimationFrame(() => {
      secondAnimationFrameId = window.requestAnimationFrame(collectDomFieldOrder);
    });

    timeoutId = window.setTimeout(collectDomFieldOrder, 220);

    return () => {
      isCancelled = true;
      window.cancelAnimationFrame(animationFrameId);
      window.cancelAnimationFrame(secondAnimationFrameId);
      window.clearTimeout(timeoutId);
    };
  }, [currentWizard, visibleTasks.length]);

  useEffect(() => {
    if (!visibleTasks.length) {
      setSelectedTaskKey("");
      return;
    }

    const hasCurrentSelection = visibleTasks.some((task) => task.taskKey === selectedTaskKey);
    if (!hasCurrentSelection && selectedTaskKey) {
      setSelectedTaskKey("");
    }
  }, [selectedTaskKey, visibleTasks]);

  useEffect(() => {
    if (selectedTaskKey || !actionableTasks.length) {
      if (selectedTaskKey || !visibleTasks.length) {
        return;
      }

      if (!isInitialSelectionReady) {
        return;
      }

      const firstVisibleTaskKey = (currentWizardVisibleTasks[0] || visibleTasks[0])?.taskKey;
      if (firstVisibleTaskKey) {
        setSelectedTaskKey(firstVisibleTaskKey);
      }
      return;
    }

    if (!isInitialSelectionReady) {
      return;
    }

    const firstPreferredTaskKey = (
      currentWizardVisibleTasks[0]
      || currentWizardActionableTasks[0]
      || actionableTasks[0]
      || visibleTasks[0]
    )?.taskKey;
    if (firstPreferredTaskKey) {
      setSelectedTaskKey(firstPreferredTaskKey);
    }
  }, [
    actionableTasks,
    currentWizardActionableTasks,
    currentWizardVisibleTasks,
    isInitialSelectionReady,
    selectedTaskKey,
    visibleTasks
  ]);

  const selectedTaskIndex = useMemo(
    () => visibleTasks.findIndex((task) => task.taskKey === selectedTaskKey),
    [selectedTaskKey, visibleTasks]
  );

  const selectedTask = useMemo(
    () => visibleTasks.find((task) => task.taskKey === selectedTaskKey) || null,
    [selectedTaskKey, visibleTasks]
  );

  const persistNextFormReview = useCallback(async (updater, { quiet = false } = {}) => {
    const currentReview = formReviewRef.current || { application_id: "", tasks: [] };
    const nextReview = typeof updater === "function" ? updater(currentReview) : updater;

    formReviewRef.current = nextReview;
    setFormReview(nextReview);

    try {
      return await persistFormReview(nextReview);
    } catch (error) {
      if (!quiet) {
        onPersistError?.(error);
      }
      throw error;
    }
  }, [onPersistError, persistFormReview, setFormReview]);

  const setMatchingTasksStatus = useCallback(async ({
    matches,
    nextStatus,
    quiet = false,
  }) => {
    if (typeof matches !== "function") {
      return formReviewRef.current;
    }

    const currentReview = formReviewRef.current || { application_id: "", tasks: [] };
    const tasks = Array.isArray(currentReview?.tasks) ? currentReview.tasks : [];
    let didChange = false;

    const nextReview = {
      ...currentReview,
      tasks: tasks.map((task) => {
        const derivedTask = withDerivedTaskMeta(task, fieldDefinitions);
        if (!matches(derivedTask, task)) {
          return task;
        }

        if (task?.status === nextStatus) {
          return task;
        }

        didChange = true;
        return {
          ...task,
          status: nextStatus,
          updatedAt: Date.now(),
        };
      }),
    };

    if (!didChange) {
      return currentReview;
    }

    try {
      return await persistNextFormReview(nextReview, { quiet });
    } catch (error) {
      return nextReview;
    }
  }, [fieldDefinitions, persistNextFormReview]);

  const resolveTaskByReference = useCallback((taskOrKey) => {
    if (!taskOrKey) {
      return null;
    }

    if (typeof taskOrKey === "string") {
      return visibleTasks.find((task) => task.taskKey === taskOrKey) || null;
    }

    return visibleTasks.find((task) => task.taskKey === taskOrKey?.taskKey)
      || visibleTasks.find((task) => buildReviewTaskKey(taskOrKey) === task.taskKey)
      || null;
  }, [visibleTasks]);

  const selectTask = useCallback((taskOrKey) => {
    const task = resolveTaskByReference(taskOrKey);
    if (task?.taskKey) {
      setSelectedTaskKey(task.taskKey);
    }
    return task;
  }, [resolveTaskByReference]);

  const goToTask = useCallback((taskOrKey) => {
    const task = selectTask(taskOrKey);
    if (!task) {
      return null;
    }

    if (task?.wizard && task.wizard !== currentWizard) {
      onNavigateToWizard?.(task.wizard, task);
      window.setTimeout(() => scheduleReviewFieldFocus(task?.fieldId), 220);
    } else {
      window.setTimeout(() => scheduleReviewFieldFocus(task?.fieldId), 40);
    }

    return task;
  }, [currentWizard, onNavigateToWizard, selectTask]);

  const resolveOffsetTask = useCallback((offset) => {
    if (!navigationTasks.length) {
      return null;
    }

    const navigationSelectedTaskIndex = navigationTasks.findIndex(
      (task) => task.taskKey === selectedTaskKey
    );

    if (navigationSelectedTaskIndex < 0) {
      return offset >= 0
        ? actionableTasks[0]
          || navigationTasks[0]
        : actionableTasks[actionableTasks.length - 1]
          || navigationTasks[navigationTasks.length - 1];
    }

    const targetIndex = navigationSelectedTaskIndex + offset;
    if (targetIndex < 0 || targetIndex >= navigationTasks.length) {
      return null;
    }

    return navigationTasks[targetIndex];
  }, [
    actionableTasks,
    navigationTasks,
    selectedTaskKey,
  ]);

  const goToOffsetTask = useCallback((offset) => {
    const targetTask = resolveOffsetTask(offset);
    if (!targetTask) {
      return null;
    }

    return goToTask(targetTask);
  }, [goToTask, resolveOffsetTask]);

  const peekVisibleTasks = useCallback(() => (
    deriveVisibleReviewTasks(
      formReviewRef.current,
      fieldDefinitions,
      fieldOrderMap,
      arrayCollectionOrderMap,
      arrayRowOrderMaps,
      "",
      new Map(),
      wizardOrderMap,
    )
  ), [arrayCollectionOrderMap, arrayRowOrderMaps, fieldDefinitions, fieldOrderMap, wizardOrderMap]);

  const peekActionableTasks = useCallback((wizard = null) => (
    peekVisibleTasks().filter((task) => {
      if (task?.status !== REVIEW_TASK_STATUS.ACTIONABLE) {
        return false;
      }

      if (!wizard) {
        return true;
      }

      if (wizard === currentWizard) {
        return isCurrentPageTask(task);
      }

      return (task?.wizard || "") === wizard;
    })
  ), [currentWizard, isCurrentPageTask, peekVisibleTasks]);

  const peekPreferredActionableTask = useCallback((wizard = null) => {
    const currentActionableTasks = peekActionableTasks(wizard);
    if (!currentActionableTasks.length) {
      return null;
    }

    return currentActionableTasks.find((task) => task?.taskKey === selectedTaskKey)
      || currentActionableTasks[0]
      || null;
  }, [peekActionableTasks, selectedTaskKey]);

  const commitFieldTask = useCallback(async ({
    fieldPath,
    fieldId,
    arrayIndexing = null,
    wizard = "",
    nextValue,
  }) => {
    if (!fieldPath) {
      return formReviewRef.current;
    }

    const normalizedFieldPath = normalizeTaskFieldPath(fieldPath);
    const currentReview = formReviewRef.current || { application_id: "", tasks: [] };
    const matchingTasks = resolveMatchingFieldTasks(
      currentReview?.tasks,
      normalizedFieldPath,
      fieldId,
      {
        arrayIndexing,
        wizard,
      }
    );

    const matchingTask = matchingTasks[0];

    if (!matchingTask) {
      return currentReview;
    }

    const matchingTaskKeys = new Set(
      matchingTasks
        .map((task) => task?.taskKey || buildReviewTaskKey(task))
        .filter(Boolean)
    );

    const nextStatus = areFieldValuesEqual(normalizedFieldPath, nextValue, matchingTask?.forValue)
      ? REVIEW_TASK_STATUS.ACTIONABLE
      : REVIEW_TASK_STATUS.COMPLETED;

    return setMatchingTasksStatus({
      matches: (task) => matchingTaskKeys.has(task?.taskKey || buildReviewTaskKey(task)),
      nextStatus,
    });
  }, [setMatchingTasksStatus]);

  const markTasksRespondedByKind = useCallback(async (taskKind, options = {}) => {
    if (!taskKind) {
      return formReviewRef.current;
    }

    return setMatchingTasksStatus({
      matches: (task) => task?.kind === taskKind && REVIEW_VISIBLE_TASK_STATUSES.includes(task?.status),
      nextStatus: REVIEW_TASK_STATUS.AWAITING_REVIEW,
      quiet: options?.quiet,
    });
  }, [setMatchingTasksStatus]);

  const markVisibleTasksResponded = useCallback(async (options = {}) => {
    return setMatchingTasksStatus({
      matches: (task) => REVIEW_VISIBLE_TASK_STATUSES.includes(task?.status),
      nextStatus: REVIEW_TASK_STATUS.AWAITING_REVIEW,
      quiet: options?.quiet,
    });
  }, [setMatchingTasksStatus]);

  return {
    visibleTasks,
    actionableTasks,
    completedTasks,
    currentWizardVisibleTasks,
    currentWizardActionableTasks,
    groupedTasks,
    currentWizard,
    hasVisibleTasks: visibleTasks.length > 0,
    hasActionableTasks: actionableTasks.length > 0,
    hasCurrentWizardActionableTasks: currentWizardActionableTasks.length > 0,
    canSubmitReview: actionableTasks.length === 0,
    selectedTask,
    selectedTaskIndex,
    selectedTaskKey,
    selectTask,
    goToTask,
    getNextTask: () => resolveOffsetTask(1),
    getPreviousTask: () => resolveOffsetTask(-1),
    goToNextTask: () => goToOffsetTask(1),
    goToPreviousTask: () => goToOffsetTask(-1),
    peekVisibleTasks,
    peekActionableTasks,
    peekPreferredActionableTask,
    peekCurrentWizardActionableTasks: () => peekActionableTasks(currentWizard || ""),
    commitFieldTask,
    markTasksRespondedByKind,
    markVisibleTasksResponded,
  };
};
